import React, { useState, useEffect, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import {
  Box,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Alert,
  InputLabel,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Business, Security, SupervisedUserCircle } from '@mui/icons-material';
import AuthLayout from './AuthLayout';

const UserTypeGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px',
  marginBottom: '24px',
}));

const TypeCard = styled(Box)(({ theme, selected }) => ({
  padding: '12px',
  borderRadius: '16px',
  border: selected ? '2px solid var(--artemy-accent)' : '1px solid var(--artemy-border)',
  background: selected ? 'rgba(61, 127, 255, 0.08)' : 'var(--artemy-bg-elevated)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  '&:hover': {
    borderColor: selected ? 'var(--artemy-accent)' : 'var(--artemy-border-bright)',
  }
}));

const IconWrapper = styled(Box)(({ selected }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: selected ? 'var(--artemy-accent)' : 'rgba(139, 150, 180, 0.1)',
  color: selected ? '#fff' : 'var(--artemy-text-secondary)',
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': { borderColor: 'var(--artemy-border)' },
    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' },
  },
  '& .MuiInputLabel-root': { color: 'var(--artemy-text-dim)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--artemy-accent)' },
  marginBottom: '16px',
});

const STORAGE_KEYS = {
  REGISTRATION_DATA: 'registration_form_data',
  REGISTRATION_STATE: 'registration_state',
  REGISTRATION_ERROR: 'registration_error',
  REGISTRATION_ACTIVE: 'registration_flow_active'
};

const Register = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const registrationActiveRef = useRef(false);
  const [usernameError, setUsernameError] = useState(false);
  const [user, setUser] = useState({
    username: '',
    email: '',
    code: '',
    password: '',
    usertype: '',
    authCode: '',
    confirmpassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.REGISTRATION_ACTIVE, 'true');
    registrationActiveRef.current = true;
    return () => {
      mountedRef.current = false;
      if (!confirm && !user.username) sessionStorage.removeItem(STORAGE_KEYS.REGISTRATION_ACTIVE);
    };
  }, []);

  const handleInputChange = (e) => {
    const fieldName = e.target.name || e.target.id;
    const value = e.target.value;
    if (fieldName === 'username') {
      const isValid = !value.includes(' ');
      setUsernameError(!isValid && value.length > 0);
    }
    setUser(prev => ({ ...prev, [fieldName]: value }));
    setError(null);
  };

  const selectUserType = (type) => {
    setUser(prev => ({ ...prev, usertype: type }));
    setError(null);
  };

  const validateForm = () => {
    if (!user.usertype) { setError('Please select a user type'); return false; }
    if (!user.username) { setError('Please enter a username'); return false; }
    if (usernameError) { setError('Username cannot contain spaces'); return false; }
    if (!user.code) { setError('Please enter an invite code'); return false; }
    if (!user.email) { setError('Please enter an email'); return false; }
    if (!user.password) { setError('Please enter a password'); return false; }
    if (user.password !== user.confirmpassword) { setError('Passwords do not match'); return false; }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const typeMap = { candidate: 1, manager: 0, stakeholder: 4, customer: 3 };
      const registrationResponse = await axios.post(process.env.REACT_APP_CANDIDATESIGNUP, {
        usertype: typeMap[user.usertype],
        email: user.email,
        code: user.code,
      });

      if (registrationResponse.data.status === 200) {
        await Auth.signUp({ 
          username: user.username, 
          password: user.password, 
          attributes: { email: user.email } 
        });
        setConfirm(true);
        setError(null);
      } else {
        setError(registrationResponse.data.data || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (event) => {
    event.preventDefault();
    if (!user.authCode) { setError('Please enter the confirmation code'); return; }
    setLoading(true);
    try {
      const confirmResult = await Auth.confirmSignUp(user.username, user.authCode);
      if (confirmResult === 'SUCCESS') {
        alert('Verification successful!');
        history.push('/login');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout 
      title={confirm ? "Verify Account" : "Create Account"} 
      subtitle={confirm ? `Enter the code sent to ${user.email}` : "Join artemy.ai and start building today"}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', bgcolor: 'rgba(255, 77, 106, 0.1)', color: 'var(--artemy-red)', border: '1px solid rgba(255, 77, 106, 0.2)' }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ color: 'var(--artemy-accent)' }} />
        </Box>
      )}

      {!confirm ? (
        <form onSubmit={handleSubmit}>
          <Typography variant="body2" sx={{ color: 'var(--artemy-text-dim)', mb: 1.5, ml: 0.5, fontWeight: 500 }}>
            Choose your role
          </Typography>
          <UserTypeGrid>
            {[
              { id: 'candidate', label: 'Talent', icon: <Person fontSize="small" /> },
              { id: 'manager', label: 'Manager', icon: <Business fontSize="small" /> },
              { id: 'stakeholder', label: 'Stakeholder', icon: <Security fontSize="small" /> },
              { id: 'customer', label: 'Admin', icon: <SupervisedUserCircle fontSize="small" /> }
            ].map((type) => (
              <TypeCard 
                key={type.id} 
                selected={user.usertype === type.id}
                onClick={() => selectUserType(type.id)}
              >
                <IconWrapper selected={user.usertype === type.id}>
                  {type.icon}
                </IconWrapper>
                <Typography variant="caption" sx={{ fontWeight: 600, color: user.usertype === type.id ? '#fff' : 'var(--artemy-text-primary)' }}>
                  {type.label}
                </Typography>
              </TypeCard>
            ))}
          </UserTypeGrid>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <StyledTextField fullWidth id="username" label="Username" value={user.username} onChange={handleInputChange} required />
            <StyledTextField fullWidth id="code" label="Invite Code" value={user.code} onChange={handleInputChange} required />
          </Box>

          <StyledTextField fullWidth id="email" label="Email Address" type="email" value={user.email} onChange={handleInputChange} required />
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <StyledTextField 
              fullWidth id="password" label="Password" type={showPassword ? "text" : "password"} value={user.password} onChange={handleInputChange} required 
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} size="small" sx={{ color: 'var(--artemy-text-dim)' }}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <StyledTextField fullWidth id="confirmpassword" label="Confirm" type={showPassword ? "text" : "password"} value={user.confirmpassword} onChange={handleInputChange} required />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.5,
              mt: 1,
              mb: 3,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: 'var(--artemy-accent) !important',
              boxShadow: '0 8px 24px rgba(61, 127, 255, 0.25)',
              '&:hover': { transform: 'translateY(-2px)' }
            }}
          >
            Create Account
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'var(--artemy-text-secondary)' }}>
              Already registered?{' '}
              <Link to="/login" style={{ color: 'var(--artemy-accent)', textDecoration: 'none', fontWeight: 700 }}>
                Log in
              </Link>
            </Typography>
          </Box>
        </form>
      ) : (
        <form onSubmit={handleConfirmSignUp}>
          <StyledTextField
            fullWidth
            id="authCode"
            label="6-Digit Verification Code"
            value={user.authCode}
            onChange={handleInputChange}
            required
            placeholder="000000"
            inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '8px', fontSize: '20px', fontWeight: 700 } }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: 'var(--artemy-accent) !important',
            }}
          >
            Verify & Finish
          </Button>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button onClick={() => setConfirm(false)} size="small" sx={{ color: 'var(--artemy-text-dim)', textTransform: 'none' }}>
              Back to registration
            </Button>
          </Box>
        </form>
      )}
    </AuthLayout>
  );
};

export default Register;